import React, { useEffect, useState } from "react";
import { Users, Award, MapPin, Briefcase, Mail, Phone, CheckCircle2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate, initials } from "@/lib/format";
import { PageHeader, Loading, SectionCard } from "@/components/common";
import { Modal } from "@/components/Modal";

export default function Consultants() {
  const [rows, setRows] = useState([]);
  const [services, setServices] = useState({});
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    (async () => {
      const [c, s] = await Promise.all([api.get("/consultants"), api.get("/service-types")]);
      setRows(c.data);
      setServices(Object.fromEntries(s.data.map((x) => [x.id, x.name])));
      setLoading(false);
    })();
  }, []);

  if (loading) return <Loading />;
  const soon = (d) => d && new Date(d) < new Date(Date.now() + 60 * 864e5);

  return (
    <div>
      <PageHeader title="Consultants & Technicians" subtitle="Skills, certifications, coverage and availability" icon={Users} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((c) => (
          <button key={c.id} onClick={() => setDetail(c)} data-testid={`consultant-card-${c.id}`}
            className="text-left bg-white border border-slate-200 rounded-xl p-4 hover:shadow-sm transition-shadow">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-full flex items-center justify-center text-white font-semibold" style={{ backgroundColor: c.calendar_colour }}>{initials(c.name)}</div>
              <div className="flex-1">
                <div className="font-medium text-slate-800">{c.name}</div>
                <div className="text-xs text-slate-400 flex items-center gap-1"><MapPin size={11} /> {c.home_location} · {c.coverage_area}</div>
              </div>
              {c.on_leave_today ? <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-semibold">On leave</span>
                : <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">{c.jobs_today} today</span>}
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {(c.skills || []).slice(0, 4).map((s) => <span key={s} className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{s}</span>)}
            </div>
            <div className="mt-3 flex items-center gap-3 text-xs text-slate-400">
              <span className="flex items-center gap-1"><Briefcase size={12} /> Max {c.max_jobs_per_day}/day</span>
              <span className="flex items-center gap-1"><Award size={12} /> {(c.certifications || []).length} certs</span>
              {(c.certifications || []).some((x) => soon(x.expiry)) && <span className="flex items-center gap-1 text-amber-600"><AlertTriangle size={12} /> expiring</span>}
            </div>
          </button>
        ))}
      </div>

      {detail && (
        <Modal open onOpenChange={() => setDetail(null)} title={detail.name} wide description={`${detail.employment_status} · ${detail.coverage_area}`} testId="consultant-detail">
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <Info label="Email" icon={Mail} value={detail.email} />
              <Info label="Telephone" icon={Phone} value={detail.telephone} />
              <Info label="Working days" value={(detail.working_days || []).join(", ")} />
              <Info label="Hours" value={`${detail.work_start} – ${detail.work_end}`} />
              <Info label="Capacity" value={`${detail.max_jobs_per_day} jobs / ${detail.max_hours_per_day}h per day`} />
              <Info label="Travel" value={`Up to ${detail.max_travel_km} km`} />
            </div>
            <div className="space-y-3">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">Skills & competencies</div>
                <div className="flex flex-wrap gap-1.5">{(detail.skills || []).map((s) => <span key={s} className="text-xs bg-seq-green/10 text-seq-green px-2 py-0.5 rounded-full">{s}</span>)}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">Authorised services</div>
                <div className="flex flex-wrap gap-1.5">{(detail.authorised_service_types || []).map((id) => <span key={id} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{services[id] || id}</span>)}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">Certifications</div>
                <div className="space-y-1">{(detail.certifications || []).map((c, i) => (
                  <div key={i} className="flex items-center justify-between text-xs"><span className="text-slate-600">{c.name}</span><span className={new Date(c.expiry) < new Date(Date.now() + 60 * 864e5) ? "text-amber-600 font-medium" : "text-slate-400"}>{fmtDate(c.expiry)}</span></div>
                ))}</div>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
const Info = ({ label, value, icon: Icon }) => (
  <div className="flex items-center justify-between"><span className="text-slate-400 flex items-center gap-1.5">{Icon && <Icon size={13} />}{label}</span><span className="text-slate-700">{value || "—"}</span></div>
);
